import { makeT, esc } from "../i18n";

const t = makeT("auth");

/*
 * Cobuntu Auth Widget v1
 *
 * Drop-in sign-in / profile UI for white-label community landing pages.
 *
 * Two states:
 *   - Logged out → rendered as a button (default label "Sign in").
 *     Click navigates to /login?next=<current path>. Cobuntu's auth
 *     flow handles the round-trip + the cookie write.
 *   - Logged in → rendered as an avatar + name. Click opens a profile
 *     drawer (My library / My orders / Settings / Sign out).
 *
 * State detection: reads the `cobuntu_auth` cookie set by the
 * community-app login flow (see lib/auth-client.ts in the main repo).
 * Cookie is host-scoped to the customer's apex, so the widget on
 * foodieslx.com / belaescala.com / etc. reads the same cookie cobuntu
 * pages do — single sign-on across the landing + the cobuntu pages.
 *
 * Three levels of customization (see docs/features/cobuntu-auth-widget.md):
 *   Level 1 — CSS custom properties on the mount node
 *   Level 2 — data-* attributes (variant, size, labels, show-icon, etc.)
 *   Level 3 — render-yourself slot pattern (data-cobuntu-show / -bind /
 *             -action attributes on customer-provided HTML inside the
 *             mount node — widget binds state + behavior, customer
 *             owns every pixel)
 *
 * Contract: see docs/features/cobuntu-auth-widget.md. v1 frozen once
 * shipped to a customer in production. Public-API discipline.
 */

// ─── Types ────────────────────────────────────────────────────────

interface JwtPayload {
  userId: string;
  email?: string;
  name?: string;
  iat?: number;
  exp?: number;
}

interface UserProfile {
  // /api/users/me returns `id`; some other surfaces use `userId`. Accept both.
  userId?: string;
  id?: string;
  usertag?: string | null;
  name: string | null;
  email: string | null;
  profileImage?: string | null;
  imageUrl?: string | null;
  // Cobuntu Premium subscriber (from /api/users/me). Drives the gold seal
  // next to the display name in the header label + account drawer. Absent
  // on the JWT, so the header badge only appears once the /me fetch lands.
  isPremium?: boolean;
}

// ─── Constants ────────────────────────────────────────────────────

const COOKIE_NAME = "cobuntu_auth";
const MOUNT_SELECTOR = "[data-cobuntu-auth]";
const MOUNTED_ATTR = "data-cobuntu-auth-mounted";
const STYLE_ID = "cobuntu-auth-widget-styles";
// v2: the v1 cache stored the wrong-shaped profile ({ user: {...} } passed
// through verbatim), so bump the key to drop those stale entries instantly
// rather than waiting out the 5-min TTL after the parse fix below.
const PROFILE_CACHE_KEY = "cobuntu_auth_profile_v2";
const PROFILE_TTL_MS = 5 * 60 * 1000;
const EXP_SKEW_MS = 30_000;

import { authedFetch } from "../api/authedFetch";
import { getCachedSession, primeSession } from "../shared/session";
import { clearCanonicalAuthCookieAllScopes, clearServerSession } from "../shared/cookie";
import { appNav } from "../shared/origin";
// Shared seeded-persona generator — same face the React app renders, so the
// widget's avatar fallback matches the user's persona everywhere else.
import { buildPersonaSvgString } from "../../../../lib/persona/personaSvg";


// ─── State ────────────────────────────────────────────────────────

let drawerEl: HTMLElement | null = null;
let backdropEl: HTMLElement | null = null;
let profilePromise: Promise<UserProfile | null> | null = null;
// Remembered so the Legal sub-view's Back button can re-render the main menu
// with the same user (avatar/name) instead of reverting to the bare JWT render.
let lastUser: JwtPayload | UserProfile | null = null;
// Which drawer view is showing, so (a) the async profile re-render never clobbers
// the Legal sub-view and (b) transitions animate in the right direction.
let currentView: "main" | "legal" = "main";
// Track if logout is in progress to show loading state
let loggingOut = false;

/**
 * Is this visitor signed in? (app platform S3.)
 *
 * Reads the SERVER-resolved session rather than decoding the auth cookie here.
 * S5 makes that cookie httpOnly, so document.cookie returns nothing and the
 * old decode would leave the Sign-in button showing to already-signed-in
 * members — the same class of bug as PBN 2026-06-11, but on every visitor
 * rather than the few holding a legacy cookie name.
 *
 * Synchronous on purpose: mountAll() defers until the session is known.
 */
function isAuthenticated(): boolean {
  return getCachedSession()?.authenticated === true;
}

/**
 * The identity we can render before the lazy /me profile fetch lands.
 *
 * Was decoded from the JWT; now comes from the session, which the server
 * already resolved. Same shape, so every consumer below is unchanged.
 */
function getJwtUser(): JwtPayload | null {
  const u = getCachedSession()?.user;
  if (!u) return null;
  return {
    userId: u.userId ?? "",
    email: u.email ?? undefined,
    name: u.name ?? undefined,
  };
}

// ─── Profile fetch (lazy — only when drawer opens) ────────────────

// Synchronous read of the cached /me profile (sessionStorage only) — lets the
// initial render decide up-front whether we already know the user (skip the
// loading skeleton) or must show it while /me resolves. Returns null when
// absent, stale, or corrupt.
function readCachedProfile(): UserProfile | null {
  try {
    const raw = sessionStorage.getItem(PROFILE_CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw) as { ts: number; profile: UserProfile };
    if (Date.now() - cached.ts < PROFILE_TTL_MS) return cached.profile;
  } catch {
    /* ignore */
  }
  return null;
}

// Whether we already have a real identity to render, so the initial paint can
// skip the loading skeleton: a pinned custom label, a cached /me from earlier
// this session, or a name carried on the JWT. Pure — unit-tested without a DOM.
function hasKnownIdentity(
  cached: UserProfile | null,
  jwt: JwtPayload | null,
  customLabel: string | null,
): boolean {
  return !!(customLabel || cached || jwt?.name?.trim());
}

async function fetchProfile(force = false): Promise<UserProfile | null> {
  if (!isAuthenticated()) return null;

  // Session cache (memory + sessionStorage) to avoid /me hits on every
  // panel open. JWT data covers initial render; this fills in avatar.
  if (!force) {
    const cached = readCachedProfile();
    if (cached) return cached;
  }

  if (!profilePromise) {
    profilePromise = (async () => {
      if (!isAuthenticated()) return null;
      try {
        // Same-origin relative path (v0.5.0+). The community-app middleware
        // forwards to api.cobuntu.com server-side with the visitor's apex
        // cookie attached.
        //
        // Hit /api/users/me — the canonical identity endpoint the React app
        // also hydrates from. getAuthenticatedUser returns the user object
        // flat: { id, name, email, usertag, profileImage, … }, so the parse
        // below (body.data ?? body) reads name/usertag/profileImage directly.
        //
        // History: a prior change pointed this at /api/users/me/identity, but
        // NO such route exists on the gateway/core (core only has /users/me).
        // The widget's fetch 404'd → returned null → fell back to "Account" +
        // the seeded persona even for users with a real name + photo. Reverted
        // 2026-06-29.
        // No authToken: same-origin, so the browser attaches the cookie and the
        // apex proxy forwards it server-side. Works under httpOnly; a JS-read
        // Bearer would not.
        const res = await authedFetch(`/api/users/me`);
        if (!res.ok) return null;
        const body = (await res.json()) as { user?: UserProfile; data?: UserProfile } | UserProfile;
        // /api/users/me wraps the record: { user: { id, name, usertag,
        // profileImage, … } } (see core getAuthenticatedUser). Other surfaces
        // used { data: {...} } or returned it flat — accept all three. The
        // prior code read `body` directly, handing back { user: … } whose
        // top level has no usertag/name/photo, so displayLabel/avatar fell
        // back to "Account" + the seeded persona. THIS was the actual cause
        // of the long-standing "header still says Account" bug.
        const profile = (
          (body as { user?: UserProfile }).user ??
          (body as { data?: UserProfile }).data ??
          (body as UserProfile)
        );
        try {
          sessionStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify({ ts: Date.now(), profile }));
        } catch {
          /* ignore */
        }
        return profile;
      } catch {
        return null;
      }
    })();
    profilePromise.finally(() => {
      profilePromise = null;
    });
  }
  return profilePromise;
}

// ─── Initials fallback when no avatar image ───────────────────────

function initials(name: string | null | undefined, email: string | null | undefined): string {
  if (name && name.trim()) {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  if (email && email.trim()) return email.trim().slice(0, 2).toUpperCase();
  return "U";
}

// Persona seed — MUST mirror the React UserAvatar precedence
// (usertag → email → id → lowercased name → "cobuntu-user") so the widget
// renders the SAME face the rest of the app shows for this user.
function personaSeed(u: { usertag?: string | null; email?: string | null; userId?: string | null; id?: string | null; name?: string | null } | null): string {
  return (
    u?.usertag ||
    u?.email ||
    u?.userId ||
    u?.id ||
    (u?.name ? u.name.toLowerCase() : null) ||
    "cobuntu-user"
  );
}

// Gold verified-premium seal — the SAME platform signal the React app renders
// (components/shared/VerifiedBadge). Vanilla HTML string so it can be injected
// next to the display name in the header label + account-drawer header. Gated
// by the caller on the user's isPremium (from /api/users/me).
function premiumBadgeMarkup(): string {
  return `<svg class="cobuntu-auth-premium" width="13" height="13" viewBox="0 0 24 24" role="img" aria-label="${esc(t("verifiedPremium", "Verified premium member"))}" style="display:inline-block;flex-shrink:0;vertical-align:middle;margin-left:3px">
  <path d="M12 1.6l2.3 1.4 2.7-.3 1.2 2.4 2.4 1.2-.3 2.7 1.4 2.3-1.4 2.3.3 2.7-2.4 1.2-1.2 2.4-2.7-.3L12 22.4l-2.3-1.4-2.7.3-1.2-2.4-2.4-1.2.3-2.7L2.3 12l1.4-2.3-.3-2.7 2.4-1.2 1.2-2.4 2.7.3z" fill="#d8b25a"></path>
  <path d="M7.6 12.3l3 3 5.8-6.2" fill="none" stroke="#20180a" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
</svg>`;
}

/*
 * The build line under Sign out, e.g. `Version 29012f8 · 23 Sep 2026`.
 *
 * The string arrives FINISHED on `window.__COBUNTU_BUILD_LINE__`, set by the
 * community app's WidgetScripts. This widget has no i18n and no build-time
 * access to the deployed commit, and baking either in would rewrite all ten
 * widget bundles every day (see packages/widgets/scripts/build.mjs on why the
 * build date was deliberately kept out of them).
 *
 * Absent means we are NOT on the community app — a customer's own site
 * embedding this widget. Render nothing there: it is the community app's
 * version, and a build hash under someone else's logo answers no question
 * they have.
 *
 * Escaped on the way in. The value is the app's own translated label today,
 * but this is the only place it becomes HTML and a global is writable by
 * anything else on the page.
 */
function buildLineMarkup(): string {
  const line = (window as any).__COBUNTU_BUILD_LINE__;
  if (typeof line !== "string" || !line.trim()) return "";
  const safe = line
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return `<p class="cobuntu-auth-drawer__build">${safe}</p>`;
}

// The header label: the user's @username, falling back to their name, then a
// generic "Account" (only before the profile resolves).
function displayLabel(u: { usertag?: string | null; name?: string | null } | null): string {
  if (u?.usertag) return `@${u.usertag}`;
  if (u?.name && u.name.trim()) return u.name.trim();
  return "Account";
}

// Avatar markup: the user's photo if present, otherwise the seeded persona.
// Accepts both field names the API surfaces use (profileImage / imageUrl).
function avatarMarkup(u: { usertag?: string | null; email?: string | null; userId?: string | null; id?: string | null; name?: string | null; profileImage?: string | null; imageUrl?: string | null } | null): string {
  const img = u?.profileImage ?? u?.imageUrl;
  if (img) return `<img class="cobuntu-auth-avatar__img" src="${escapeAttr(img)}" alt="">`;
  return buildPersonaSvgString(personaSeed(u));
}

// ─── Slot pattern (Level 3 — customer-provided HTML) ──────────────

function hasCustomSlots(host: HTMLElement): boolean {
  return !!host.querySelector("[data-cobuntu-show]");
}

function applySlotState(host: HTMLElement): void {
  const authed = isAuthenticated();
  const jwt = authed ? getJwtUser() : null;

  // Toggle visibility per data-cobuntu-show.
  host.querySelectorAll<HTMLElement>("[data-cobuntu-show]").forEach((el) => {
    const want = el.getAttribute("data-cobuntu-show");
    const visible = (want === "logged-in" && authed) || (want === "logged-out" && !authed);
    el.style.display = visible ? "" : "none";
  });

  // Bind data values per data-cobuntu-bind.
  host.querySelectorAll<HTMLElement>("[data-cobuntu-bind]").forEach((el) => {
    const key = el.getAttribute("data-cobuntu-bind");
    if (key === "user-name") el.textContent = jwt?.name || "";
    else if (key === "user-email") el.textContent = jwt?.email || "";
    else if (key === "user-initials") el.textContent = initials(jwt?.name, jwt?.email);
    else if (key === "user-avatar") {
      // Filled in lazily by fetchProfile when the drawer opens.
      el.setAttribute("data-cobuntu-needs-avatar", "");
    }
  });

  // Wire actions.
  host.querySelectorAll<HTMLElement>("[data-cobuntu-action]").forEach((el) => {
    if (el.getAttribute("data-cobuntu-action-bound") === "1") return;
    el.setAttribute("data-cobuntu-action-bound", "1");
    const action = el.getAttribute("data-cobuntu-action");
    el.addEventListener("click", (e) => {
      e.preventDefault();
      if (action === "open-profile") openDrawer();
      else if (action === "sign-out") handleSignOut();
      else if (action === "sign-in") navigateToLogin();
    });
  });
}

// ─── Default render (Level 1 + 2 — no custom slots) ───────────────

function renderDefault(host: HTMLElement): void {
  const authed = isAuthenticated();
  const jwt = authed ? getJwtUser() : null;

  const variant = host.getAttribute("data-variant") || "outlined"; // outlined | filled | text
  const size = host.getAttribute("data-size") || "md"; // sm | md | lg
  const labelLoggedOut = host.getAttribute("data-label-logged-out") || t("signIn", "Sign in");
  const showIcon = host.getAttribute("data-show-icon") !== "false";
  const showAvatar = host.getAttribute("data-show-avatar") !== "false";
  // Opt-in presence dot on the logged-in avatar. Absent on marketing landings
  // (they share this bundle) — only surfaces I control set `data-presence`. The
  // community-app passes "online" (the viewer is using the app right now); it's
  // a pure self-view and never reveals anyone else's status. Valid: online |
  // away | offline; anything else is ignored (no dot).
  const presenceAttr = host.getAttribute("data-presence");
  const presence =
    presenceAttr === "online" || presenceAttr === "away" || presenceAttr === "offline"
      ? presenceAttr
      : null;

  host.innerHTML = "";

  if (!authed) {
    const a = document.createElement("a");
    a.href = appNav(`/login?next=${encodeURIComponent(location.pathname + location.search)}`);
    // Logged-out CTA: a filled brand pill for the text style, a borderless icon
    // for the icon-only style — no outlined box.
    const signinVariant = variant === "icon" ? "signin-icon" : "signin";
    a.className = `cobuntu-auth-btn cobuntu-auth-btn--${signinVariant} cobuntu-auth-btn--${size}`;
    a.setAttribute("data-cobuntu-state", "logged-out");
    a.innerHTML = `
      ${showIcon ? signInIconSvg() : ""}
      <span class="cobuntu-auth-btn__label">${escapeHtml(labelLoggedOut)}</span>
    `;
    host.appendChild(a);
    return;
  }

  // Logged in
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = `cobuntu-auth-btn cobuntu-auth-btn--${variant} cobuntu-auth-btn--${size} cobuntu-auth-btn--user`;
  btn.setAttribute("data-cobuntu-state", "logged-in");
  const customLabel = host.getAttribute("data-label-logged-in");

  // The JWT carries only a userId — no name, @username, or photo. When we don't
  // already have the identity (a pinned label, a cached /me, or a name on the
  // JWT), render a loading skeleton — a shimmer avatar + label bar — instead of
  // the old flash of "Account" + a generic persona, then swap in the real
  // identity once /me resolves. If /me fails, the skeleton is cleared back to
  // the persona + "Account" fallback (below), so it never shimmers forever.
  const cached = readCachedProfile();
  const loading = !hasKnownIdentity(cached, jwt, customLabel);
  const initialUser = cached || jwt;
  const initialLabel = customLabel || (loading ? "" : displayLabel(initialUser));

  btn.setAttribute(
    "aria-label",
    loading ? "Loading your account" : `Open profile menu — ${initialLabel}`,
  );
  if (loading) btn.setAttribute("aria-busy", "true");

  const avatarInner = loading
    ? `<span class="cobuntu-auth-skeleton cobuntu-auth-skeleton--avatar" aria-hidden="true"></span>`
    : avatarMarkup(initialUser);
  const labelInner = loading
    ? `<span class="cobuntu-auth-skeleton cobuntu-auth-skeleton--label" aria-hidden="true"></span>`
    : escapeHtml(initialLabel);

  btn.innerHTML = `
    ${showAvatar ? `<span class="cobuntu-auth-avatar-wrap"><span class="cobuntu-auth-avatar" data-cobuntu-avatar>${avatarInner}</span>${presence ? `<span class="cobuntu-auth-avatar__presence" data-state="${presence}" aria-hidden="true"></span>` : ""}</span>` : ""}
    <span class="cobuntu-auth-btn__label" data-cobuntu-label>${labelInner}</span>
  `;
  btn.addEventListener("click", openDrawer);
  host.appendChild(btn);

  // Once /me resolves, swap in the real avatar (photo or the seeded persona on
  // the usertag seed) + the @username label + any premium seal. On failure,
  // clear the skeleton back to the persona + "Account" fallback.
  fetchProfile().then((p) => {
    const av = btn.querySelector<HTMLElement>("[data-cobuntu-avatar]");
    const lbl = btn.querySelector<HTMLElement>("[data-cobuntu-label]");
    btn.removeAttribute("aria-busy");

    if (!p) {
      // /me failed. Only repaint if we were showing the skeleton — otherwise the
      // JWT/cached render already stands.
      if (loading) {
        if (av) av.innerHTML = avatarMarkup(jwt);
        if (lbl) lbl.textContent = displayLabel(jwt);
        btn.setAttribute("aria-label", `Open profile menu — ${displayLabel(jwt)}`);
      }
      return;
    }

    if (av) av.innerHTML = avatarMarkup(p);
    if (lbl) {
      if (!customLabel) lbl.textContent = displayLabel(p);
      // The JWT carries no isPremium, so the gold seal can only appear AFTER
      // /me resolves. Inject it as a sibling right after the label when the
      // user is premium; otherwise ensure any stale badge is removed.
      const existing = btn.querySelector<HTMLElement>("[data-cobuntu-premium]");
      if (p.isPremium) {
        if (!existing) {
          const badge = document.createElement("span");
          badge.setAttribute("data-cobuntu-premium", "");
          badge.style.display = "inline-flex";
          badge.style.alignItems = "center";
          badge.innerHTML = premiumBadgeMarkup();
          lbl.insertAdjacentElement("afterend", badge);
        }
      } else if (existing) {
        existing.remove();
      }
    }
    btn.setAttribute("aria-label", `Open profile menu — ${customLabel || displayLabel(p)}`);
  });
}

function renderHost(host: HTMLElement): void {
  host.setAttribute(MOUNTED_ATTR, "");
  if (hasCustomSlots(host)) {
    applySlotState(host);
    // Lazy avatar fill for slot-mode too
    if (isAuthenticated()) {
      fetchProfile().then((p) => {
        if (!p?.profileImage) return;
        host.querySelectorAll<HTMLElement>("[data-cobuntu-needs-avatar]").forEach((el) => {
          if (el.tagName === "IMG") (el as HTMLImageElement).src = p.profileImage!;
          else el.innerHTML = `<img src="${escapeAttr(p.profileImage!)}" alt="">`;
          el.removeAttribute("data-cobuntu-needs-avatar");
        });
      });
    }
  } else {
    renderDefault(host);
  }
}

function rerenderAll(): void {
  document.querySelectorAll<HTMLElement>(MOUNT_SELECTOR).forEach((el) => {
    el.removeAttribute(MOUNTED_ATTR);
    renderHost(el);
  });
}

// ─── Drawer (profile panel) ───────────────────────────────────────

function ensureDrawer(): void {
  if (drawerEl && backdropEl) return;
  backdropEl = document.createElement("div");
  backdropEl.className = "cobuntu-auth-backdrop";
  backdropEl.addEventListener("click", closeDrawer);

  drawerEl = document.createElement("aside");
  drawerEl.className = "cobuntu-auth-drawer";
  drawerEl.setAttribute("role", "dialog");
  drawerEl.setAttribute("aria-modal", "true");
  drawerEl.setAttribute("aria-label", "Profile menu");
  drawerEl.tabIndex = -1;

  document.body.appendChild(backdropEl);
  document.body.appendChild(drawerEl);

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && drawerEl?.classList.contains("cobuntu-auth-drawer--open")) {
      closeDrawer();
    }
  });
}

function openDrawer(): void {
  if (!isAuthenticated()) {
    navigateToLogin();
    return;
  }
  ensureDrawer();
  renderDrawer(getJwtUser());
  requestAnimationFrame(() => {
    backdropEl!.classList.add("cobuntu-auth-backdrop--open");
    drawerEl!.classList.add("cobuntu-auth-drawer--open");
    drawerEl!.focus();
  });
  // Fetch profile in the background to fill in the real avatar + name/usertag.
  //
  // Re-render whenever a profile resolves and the drawer still exists — do NOT
  // gate on the "--open" class. When the profile is CACHED (the common case:
  // the header button already hydrated + cached it on page load), this .then
  // resolves as a microtask that runs BEFORE the requestAnimationFrame above
  // adds "--open" — so the old `contains("--open")` check was false and the
  // swap was skipped, leaving the drawer stuck on the JWT render ("Member" +
  // persona) every time. Re-rendering a hidden/closed drawer is harmless.
  fetchProfile().then((p) => {
    // Only refresh the MAIN menu — never yank the user out of the Legal sub-view
    // if they drilled in before the profile resolved.
    if (drawerEl && p && currentView === "main") renderDrawer(p);
  });
}

function closeDrawer(): void {
  if (!drawerEl || !backdropEl) return;
  backdropEl.classList.remove("cobuntu-auth-backdrop--open");
  drawerEl.classList.remove("cobuntu-auth-drawer--open");
}

/**
 * Keep the menu's scroll-fade honest.
 *
 * Writes `data-fade` on the nav so the CSS mask only ever fades an edge that
 * has content behind it: "top" once scrolled away from the start, "bottom"
 * while more remains, "both" in the middle, and nothing at all when the list
 * fits. A fade that is always there stops being information.
 *
 * Called after every render because the drawer rebuilds its own innerHTML
 * (so the previous nav and its listeners are gone), and watched with a
 * ResizeObserver because the row count changes underneath us: the drawer
 * renders once from the JWT and again from /me, which is exactly when a list
 * that fitted stops fitting.
 */
function wireNavFade(root: HTMLElement): void {
  const nav = root.querySelector<HTMLElement>(".cobuntu-auth-drawer__nav");
  if (!nav) return;

  const update = () => {
    // 1px of tolerance: fractional scroll heights on HiDPI mean scrollTop +
    // clientHeight lands a hair under scrollHeight at the true bottom, which
    // would leave the bottom fade on forever.
    const atTop = nav.scrollTop <= 1;
    const atBottom = nav.scrollTop + nav.clientHeight >= nav.scrollHeight - 1;
    const fade = atTop && atBottom ? "none" : atTop ? "bottom" : atBottom ? "top" : "both";
    if (nav.dataset.fade !== fade) nav.dataset.fade = fade;
  };

  nav.addEventListener("scroll", update, { passive: true });
  if (typeof ResizeObserver !== "undefined") {
    const ro = new ResizeObserver(update);
    ro.observe(nav);
    // The scroll height depends on the ROWS, not just the viewport, so observe
    // the content too — a re-render with one more item must re-evaluate.
    Array.from(nav.children).forEach((c) => ro.observe(c as Element));
  }
  update();
}

function renderDrawer(user: JwtPayload | UserProfile | null, anim: "" | "pop" = ""): void {
  if (!drawerEl) return;
  lastUser = user;
  currentView = "main";
  drawerEl.dataset.anim = anim;
  const name = (user && "name" in user ? user.name : null) || "";
  const usertag = (user && "usertag" in user ? user.usertag : null) || null;
  const email = (user && "email" in user ? user.email : null) || "";
  // Premium only lands on the UserProfile from /me — the JWT render is never
  // premium, so the gold seal appears on the background re-render (see
  // openDrawer's fetchProfile().then(renderDrawer)).
  const isPremium = !!(user && "isPremium" in user && user.isPremium);

  drawerEl.innerHTML = `
    <header class="cobuntu-auth-drawer__header">
      <div class="cobuntu-auth-drawer__id">
        <span class="cobuntu-auth-drawer__avatar">${avatarMarkup(user)}</span>
        <div class="cobuntu-auth-drawer__id-text">
          <div class="cobuntu-auth-drawer__name">${escapeHtml(name || "Member")}${isPremium ? premiumBadgeMarkup() : ""}</div>
          ${usertag
            ? `<div class="cobuntu-auth-drawer__email">@${escapeHtml(usertag)}</div>`
            : email ? `<div class="cobuntu-auth-drawer__email">${escapeHtml(email)}</div>` : ""}
        </div>
      </div>
      <button type="button" class="cobuntu-auth-drawer__close" aria-label="${esc(t("close", "Close"))}">
        <span class="cobuntu-auth-drawer__close-line cobuntu-auth-drawer__close-line--top"></span>
        <span class="cobuntu-auth-drawer__close-line cobuntu-auth-drawer__close-line--bottom"></span>
      </button>
    </header>
    <nav class="cobuntu-auth-drawer__nav">
      <div class="cobuntu-auth-drawer__group">${esc(t("groupAccount", "Account"))}</div>
      <a href="/hub" class="cobuntu-auth-drawer__link">
        <span class="cobuntu-auth-drawer__link-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <rect x="3" y="3" width="7" height="9" rx="1"></rect>
            <rect x="14" y="3" width="7" height="5" rx="1"></rect>
            <rect x="14" y="12" width="7" height="9" rx="1"></rect>
            <rect x="3" y="16" width="7" height="5" rx="1"></rect>
          </svg>
        </span>
        <span class="cobuntu-auth-drawer__link-text">
          <span class="cobuntu-auth-drawer__link-label">${esc(t("dashboard", "Dashboard"))}</span>
        </span>
      </a>
      <a href="/profile" class="cobuntu-auth-drawer__link">
        <span class="cobuntu-auth-drawer__link-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <circle cx="12" cy="8" r="4"></circle>
            <path d="M4 21c0-4 4-7 8-7s8 3 8 7"></path>
          </svg>
        </span>
        <span class="cobuntu-auth-drawer__link-text">
          <span class="cobuntu-auth-drawer__link-label">${esc(t("myProfile", "My profile"))}</span>
        </span>
      </a>
      <a href="/membership" class="cobuntu-auth-drawer__link">
        <span class="cobuntu-auth-drawer__link-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <rect x="2" y="5" width="20" height="14" rx="2"></rect>
            <circle cx="8" cy="11" r="2"></circle>
            <path d="M5 16c.6-1.6 4.4-1.6 5 0"></path>
            <path d="M14 10.5h4.5M14 14.5h3"></path>
          </svg>
        </span>
        <span class="cobuntu-auth-drawer__link-text">
          <span class="cobuntu-auth-drawer__link-label">${esc(t("myMembership", "My Membership"))}</span>
        </span>
      </a>
      <div class="cobuntu-auth-drawer__group">${esc(t("groupActivity", "Activity"))}</div>
      <a href="/my-events" class="cobuntu-auth-drawer__link">
        <span class="cobuntu-auth-drawer__link-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <rect x="3" y="4" width="18" height="18" rx="2"></rect>
            <path d="M16 2v4M8 2v4M3 10h18"></path>
          </svg>
        </span>
        <span class="cobuntu-auth-drawer__link-text">
          <span class="cobuntu-auth-drawer__link-label">${esc(t("myEvents", "My Events"))}</span>
        </span>
      </a>
      <a href="/my-orders" class="cobuntu-auth-drawer__link">
        <span class="cobuntu-auth-drawer__link-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4zM3 6h18"></path>
            <path d="M16 10a4 4 0 01-8 0"></path>
          </svg>
        </span>
        <span class="cobuntu-auth-drawer__link-text">
          <span class="cobuntu-auth-drawer__link-label">${esc(t("myOrders", "My Orders"))}</span>
        </span>
      </a>
      <a href="/library" class="cobuntu-auth-drawer__link">
        <span class="cobuntu-auth-drawer__link-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
          </svg>
        </span>
        <span class="cobuntu-auth-drawer__link-text">
          <span class="cobuntu-auth-drawer__link-label">${esc(t("library", "My Library"))}</span>
        </span>
      </a>
      <div class="cobuntu-auth-drawer__group">${esc(t("groupFinancials", "Financials"))}</div>
      <a href="/my-ledger" class="cobuntu-auth-drawer__link">
        <span class="cobuntu-auth-drawer__link-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <line x1="12" y1="1" x2="12" y2="23"></line>
            <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"></path>
          </svg>
        </span>
        <span class="cobuntu-auth-drawer__link-text">
          <span class="cobuntu-auth-drawer__link-label">${esc(t("ledger", "Ledger"))}</span>
        </span>
      </a>
      <a href="/my-payouts" class="cobuntu-auth-drawer__link">
        <span class="cobuntu-auth-drawer__link-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <rect x="2" y="5" width="20" height="14" rx="2"></rect>
            <path d="M2 10h20"></path>
          </svg>
        </span>
        <span class="cobuntu-auth-drawer__link-text">
          <span class="cobuntu-auth-drawer__link-label">${esc(t("payouts", "Payouts"))}</span>
        </span>
      </a>
      <a href="/my-referrals" class="cobuntu-auth-drawer__link">
        <span class="cobuntu-auth-drawer__link-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
            <path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
          </svg>
        </span>
        <span class="cobuntu-auth-drawer__link-text">
          <span class="cobuntu-auth-drawer__link-label">${esc(t("referrals", "Referrals"))}</span>
        </span>
      </a>
      <div class="cobuntu-auth-drawer__group">${esc(t("groupSystem", "System"))}</div>
      <a href="?settings=account" data-cobuntu-settings="account" class="cobuntu-auth-drawer__link">
        <span class="cobuntu-auth-drawer__link-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <circle cx="12" cy="12" r="3"></circle>
            <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"></path>
          </svg>
        </span>
        <span class="cobuntu-auth-drawer__link-text">
          <span class="cobuntu-auth-drawer__link-label">${esc(t("settings", "Settings"))}</span>
        </span>
      </a>
      <a href="?settings=api" data-cobuntu-settings="api" class="cobuntu-auth-drawer__link">
        <span class="cobuntu-auth-drawer__link-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="16 18 22 12 16 6"></polyline>
            <polyline points="8 6 2 12 8 18"></polyline>
          </svg>
        </span>
        <span class="cobuntu-auth-drawer__link-text">
          <span class="cobuntu-auth-drawer__link-label">${esc(t("api", "API"))}</span>
        </span>
      </a>
      <a href="/payment-refund-policy" data-cobuntu-legal-open class="cobuntu-auth-drawer__link">
        <span class="cobuntu-auth-drawer__link-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="8" y1="13" x2="16" y2="13"></line>
            <line x1="8" y1="17" x2="16" y2="17"></line>
          </svg>
        </span>
        <span class="cobuntu-auth-drawer__link-text">
          <span class="cobuntu-auth-drawer__link-label">${esc(t("legal", "Legal"))}</span>
        </span>
        <span class="cobuntu-auth-drawer__link-chevron" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
        </span>
      </a>
    </nav>
    <footer class="cobuntu-auth-drawer__footer">
      <button type="button" class="cobuntu-auth-drawer__signout">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
        ${esc(t("signOut", "Sign out"))}
      </button>
      ${buildLineMarkup()}
    </footer>
  `;

  wireNavFade(drawerEl);
  drawerEl.querySelector<HTMLButtonElement>(".cobuntu-auth-drawer__close")
    ?.addEventListener("click", closeDrawer);
  drawerEl.querySelector<HTMLButtonElement>(".cobuntu-auth-drawer__signout")
    ?.addEventListener("click", handleSignOut);
  // Settings opens the URL-driven settings MODAL in place (overlaying the
  // current page) — the same behaviour as the profile page's "Edit profile"
  // (router push of ?settings=<tab>), NOT a navigation to a separate /settings
  // page. We dispatch a window event the React SettingsModalHost listens for so
  // the open is client-side + smooth; the href (?settings=account) is a no-JS
  // fallback that still opens the modal on the next load.
  // querySelectorAll, not querySelector: there are TWO settings rows now
  // (Settings → account, API → api). With the single-element lookup only the
  // first in DOM order was bound, so the second rendered as a live link that
  // did nothing but append ?settings= and reload.
  drawerEl.querySelectorAll<HTMLAnchorElement>("[data-cobuntu-settings]")
    .forEach((el) => el.addEventListener("click", (e) => {
      e.preventDefault();
      const tab = (e.currentTarget as HTMLElement).getAttribute("data-cobuntu-settings") || "account";
      closeDrawer();
      window.dispatchEvent(new CustomEvent("cobuntu:open-settings", { detail: { tab } }));
    }));
  // "Legal" switches the drawer into a second state (a back button + the three
  // legal pages) instead of navigating — each sub-item is then its own route.
  drawerEl.querySelector<HTMLAnchorElement>("[data-cobuntu-legal-open]")
    ?.addEventListener("click", (e) => {
      e.preventDefault();
      renderLegalSubmenu();
    });
}

/**
 * The Legal sub-state: replaces the drawer body with a Back button + the
 * policy pages, each a plain full-route link (/payment-refund-policy, /terms,
 * /privacy, /referrals). Back re-renders the main menu with the remembered user.
 */
function renderLegalSubmenu(): void {
  if (!drawerEl) return;
  currentView = "legal";
  drawerEl.dataset.anim = "push";
  const legalLink = (href: string, label: string, sub: string) => `
      <a href="${href}" class="cobuntu-auth-drawer__link">
        <span class="cobuntu-auth-drawer__link-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="8" y1="13" x2="16" y2="13"></line>
            <line x1="8" y1="17" x2="16" y2="17"></line>
          </svg>
        </span>
        <span class="cobuntu-auth-drawer__link-text">
          <span class="cobuntu-auth-drawer__link-label">${label}</span>
          <span class="cobuntu-auth-drawer__link-sub">${sub}</span>
        </span>
      </a>`;

  drawerEl.innerHTML = `
    <header class="cobuntu-auth-drawer__header">
      <div class="cobuntu-auth-drawer__id">
        <button type="button" class="cobuntu-auth-drawer__subback" data-cobuntu-legal-back aria-label="${esc(t("back", "Back"))}">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
        </button>
        <div class="cobuntu-auth-drawer__id-text">
          <div class="cobuntu-auth-drawer__name">Legal</div>
        </div>
      </div>
      <button type="button" class="cobuntu-auth-drawer__close" aria-label="${esc(t("close", "Close"))}">
        <span class="cobuntu-auth-drawer__close-line cobuntu-auth-drawer__close-line--top"></span>
        <span class="cobuntu-auth-drawer__close-line cobuntu-auth-drawer__close-line--bottom"></span>
      </button>
    </header>
    <nav class="cobuntu-auth-drawer__nav">
      ${legalLink("/payment-refund-policy", "Payment &amp; Refund Policy", "Buying, refunds, payouts and fees")}
      ${legalLink("/terms", esc(t("legalTerms", "Terms &amp; Conditions")), esc(t("legalTermsDesc", "The rules of using this platform")))}
      ${legalLink("/privacy", esc(t("legalPrivacy", "Privacy Policy")), esc(t("legalPrivacyDesc", "How your data is used")))}
      ${legalLink("/referrals", "How referrals work", "Commissions, payouts and refund holds")}
    </nav>
  `;

  wireNavFade(drawerEl);
  drawerEl.querySelector<HTMLButtonElement>(".cobuntu-auth-drawer__close")
    ?.addEventListener("click", closeDrawer);
  drawerEl.querySelector<HTMLButtonElement>("[data-cobuntu-legal-back]")
    ?.addEventListener("click", () => renderDrawer(lastUser, "pop"));
}

// ─── Actions ──────────────────────────────────────────────────────

function navigateToLogin(): void {
  location.href = appNav(`/login?next=${encodeURIComponent(location.pathname + location.search)}`);
}

function handleSignOut(): void {
  if (loggingOut) return;
  loggingOut = true;

  // Update button state to show loading
  const signOutBtn = drawerEl?.querySelector<HTMLButtonElement>(".cobuntu-auth-drawer__signout");
  if (signOutBtn) {
    signOutBtn.disabled = true;
    signOutBtn.innerHTML = `
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="animation: spin 0.8s linear infinite; display: inline-block;"><circle cx="12" cy="12" r="10" fill="none"/><path d="M12 2a10 10 0 0 1 0 20" stroke-linecap="round"/></svg>
      <span style="margin-left: 8px;">${esc(t("loggingOut", "Logging out..."))}</span>
    `;
    if (!document.getElementById("cobuntu-auth-spinner-style")) {
      const style = document.createElement("style");
      style.id = "cobuntu-auth-spinner-style";
      style.textContent = `
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);
    }
  }

  // Clear canonical at host + shared-login domain scopes, plus legacy variants.
  // Server clear FIRST (S4) — once the cookie is httpOnly the local clear
  // below cannot touch it, and every caller reloads immediately after.
  clearServerSession();
  clearCanonicalAuthCookieAllScopes();
  try {
    const hostname = window.location.hostname;
    const tag = hostname.split(".")[0];
    if (tag && tag !== "localhost") {
      document.cookie = `cobuntu_auth_${tag}=; path=/; max-age=0`;
    }
  } catch {
    /* ignore */
  }
  try { sessionStorage.removeItem(PROFILE_CACHE_KEY); } catch { /* ignore */ }

  // Brief delay to show loading state before reload
  setTimeout(() => {
    closeDrawer();
    // Reload so SSR redoes auth-aware rendering.
    location.reload();
  }, 500);
}

// ─── Helpers ──────────────────────────────────────────────────────

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case "&": return "&amp;";
      case "<": return "&lt;";
      case ">": return "&gt;";
      case '"': return "&quot;";
      default: return "&#39;";
    }
  });
}
const escapeAttr = escapeHtml;

function signInIconSvg(): string {
  return `<svg class="cobuntu-auth-btn__icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
    <polyline points="10 17 15 12 10 7"></polyline>
    <line x1="15" y1="12" x2="3" y2="12"></line>
  </svg>`;
}

// ─── Styles ───────────────────────────────────────────────────────

const CSS = `
.cobuntu-auth-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  text-decoration: none;
  font-family: var(--cobuntu-font, inherit);
  font-size: var(--cobuntu-font-size, 13px);
  font-weight: var(--cobuntu-font-weight, 500);
  letter-spacing: var(--cobuntu-letter-spacing, normal);
  text-transform: var(--cobuntu-text-transform, none);
  color: var(--cobuntu-color, currentColor);
  background: var(--cobuntu-bg, transparent);
  border: var(--cobuntu-border, 1px solid currentColor);
  border-radius: var(--cobuntu-radius, 0);
  padding: var(--cobuntu-padding, 8px 16px);
  transition: background 0.15s ease, color 0.15s ease, opacity 0.15s ease, border-color 0.15s ease;
  box-sizing: border-box;
  line-height: 1.2;
}
.cobuntu-auth-btn--sm { font-size: 12px; padding: 6px 12px; }
.cobuntu-auth-btn--md { /* default */ }
.cobuntu-auth-btn--lg { font-size: 14px; padding: 12px 24px; }

.cobuntu-auth-btn--filled {
  background: var(--cobuntu-bg, currentColor);
  color: var(--cobuntu-color, #fff);
  border-color: transparent;
}
.cobuntu-auth-btn--text {
  background: transparent;
  border: 0;
  padding: var(--cobuntu-padding, 6px 10px);
}

/* Logged-out Sign-in CTA — a solid brand button (no outlined box). Uses the
   community's brand accent, its computed on-brand text colour, and its
   configured button shape.

   The shape used to be a hardcoded pill while the fill came from the
   community, so this button took the brand's colour and ignored its shape:
   next to an app whose buttons are 9px (SheSapiens) or square (Bela Escala),
   the header's Sign in was visibly a different control. The fallback keeps
   the pill for any community that has not configured a radius. */
.cobuntu-auth-btn--signin {
  background: var(--cobuntu-accent, currentColor);
  color: var(--cobuntu-cart-primary-text, var(--cobuntu-badge-text, #fff));
  border: 0;
  border-radius: var(--cobuntu-button-radius, 9999px);
  font-weight: 600;
}
.cobuntu-auth-btn--signin:hover { opacity: 0.92; }
/* Icon-only sign-in — borderless, with the same hover tint as the other icons. */
.cobuntu-auth-btn--signin-icon {
  background: transparent;
  border: 0;
  border-radius: 9999px;
}
.cobuntu-auth-btn--signin-icon:hover {
  background: var(--cobuntu-hover-bg, color-mix(in srgb, currentColor 10%, transparent));
}

.cobuntu-auth-btn:hover {
  background: var(--cobuntu-hover-bg, var(--cobuntu-bg, transparent));
  color: var(--cobuntu-hover-color, var(--cobuntu-color, currentColor));
  opacity: 0.92;
}

/* Logged-in account chip: no box, avatar + @username, with a visible hover
   fill (the generic hover above is a no-op when bg is transparent).

   Follows the community's button shape for the same reason Sign in does —
   this is the SAME header slot in its signed-in state, and a square-cornered
   community that got a square Sign in should not get a pill-shaped hover the
   moment someone logs in. The avatar inside stays circular: round is what
   makes it read as a person, not a brand choice. */
.cobuntu-auth-btn--user {
  border: 0;
  background: transparent;
  border-radius: var(--cobuntu-button-radius, 9999px);
  padding: 4px 12px 4px 4px;
  gap: 8px;
  font-weight: 600;
}
.cobuntu-auth-btn--user:hover {
  background: var(--cobuntu-hover-bg, color-mix(in srgb, currentColor 10%, transparent));
  opacity: 1;
}

.cobuntu-auth-btn--user .cobuntu-auth-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 9999px;
  /* The avatar now holds the user's photo or the seeded persona (both fill the
     circle), so no grey initials background. */
  background: transparent;
  overflow: hidden;
  flex-shrink: 0;
}
.cobuntu-auth-avatar__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
/* Presence wrapper: keeps the avatar its 26px circle (overflow-hidden) while
   the status dot sits OUTSIDE it as a sibling — so the /me avatar innerHTML
   swap can't wipe it, and the circle's clip can't cut it off. */
.cobuntu-auth-avatar-wrap {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
}
.cobuntu-auth-avatar__presence {
  position: absolute;
  bottom: -1px;
  right: -1px;
  width: 9px;
  height: 9px;
  border-radius: 9999px;
  /* Ring matches the header surface so the dot reads on any avatar photo.
     Landings set --cobuntu-bg (their header/trigger bg), so it wins there. The
     community-app doesn't set --cobuntu-bg on the auth mount, so it fell back
     to #fff → a white ring on dark-themed communities (Bela Escala). Add the
     community bg vars to the chain: --cobuntu-drawer-bg (= the community's
     bgColor, injected by lib/widget-theme.ts) then --bg-color (the app theme
     bg), before the #fff last resort. */
  box-shadow: 0 0 0 2px var(--cobuntu-bg, var(--cobuntu-drawer-bg, var(--bg-color, #fff)));
  pointer-events: none;
}
.cobuntu-auth-avatar__presence[data-state="online"] { background: #22c55e; }
.cobuntu-auth-avatar__presence[data-state="away"] { background: #fbbf24; }
.cobuntu-auth-avatar__presence[data-state="offline"] { background: #9ca3af; }

.cobuntu-auth-backdrop {
  position: fixed;
  inset: 0;
  /* 0.32, not 0.5. The drawer casts a soft black shadow to its left, and a
     half-black scrim leaves that shadow nothing to darken: the panel then
     separates from the page by brightness alone and reads flat. Lighter
     scrim + a small blur lets the shadow do its job. */
  background: var(--cobuntu-backdrop, rgba(0, 0, 0, 0.32));
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
  z-index: 2147483646;
}
.cobuntu-auth-backdrop--open {
  opacity: 1;
  pointer-events: auto;
}

.cobuntu-auth-drawer {
  position: fixed;
  /* A real side drawer: glued to the right edge, full height, rounded only on
     the LEFT (the edge that faces the page). */
  top: 0;
  right: 0;
  bottom: 0;
  width: min(380px, 100vw);
  background: var(--cobuntu-drawer-bg, #fff);
  color: var(--cobuntu-drawer-text, #0a0a0a);
  overflow: hidden;
  transform: translateX(100%);
  transition: transform 0.28s cubic-bezier(0.16, 1, 0.3, 1);
  z-index: 2147483647;
  display: flex;
  flex-direction: column;
  font-family: var(--cobuntu-font, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif);
  box-shadow: -24px 0 60px -14px rgba(0, 0, 0, 0.32);
  /* The 22px left radius used to be what said "separate object". Squaring the
     corners removed that cue, so a crisp hairline takes the job over: a round
     panel can live without a defined edge, a square one cannot. */
  border-left: 1px solid var(--cobuntu-drawer-edge, rgba(0, 0, 0, 0.10));
  outline: none;
}
.cobuntu-auth-drawer--open { transform: translateX(0); }

.cobuntu-auth-drawer__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 16px 16px;
  border-bottom: 1px solid var(--cobuntu-drawer-border, color-mix(in srgb, currentColor 15%, transparent));
  gap: 12px;
}
.cobuntu-auth-drawer__id {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.cobuntu-auth-drawer__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border-radius: 9999px;
  /* holds the photo or the seeded persona — both fill the circle */
  background: transparent;
  overflow: hidden;
  flex-shrink: 0;
}
.cobuntu-auth-drawer__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.cobuntu-auth-drawer__id-text { min-width: 0; }
.cobuntu-auth-drawer__name {
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cobuntu-auth-drawer__email {
  font-size: 12px;
  opacity: 0.6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cobuntu-auth-drawer__close {
  background: color-mix(in srgb, currentColor 6%, transparent);
  border: 0;
  cursor: pointer;
  color: inherit;
  width: 34px;
  height: 34px;
  border-radius: 9999px;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 0;
  flex-shrink: 0;
  transition: background 0.15s ease;
}
.cobuntu-auth-drawer__close:hover { background: color-mix(in srgb, currentColor 12%, transparent); }
.cobuntu-auth-drawer__close-line {
  display: block;
  width: 15px;
  height: 1.5px;
  border-radius: 2px;
  background: currentColor;
  transition: transform 0.3s ease;
  transform-origin: center;
}
.cobuntu-auth-drawer__close-line--top { transform: translateY(3.25px) rotate(45deg); }
.cobuntu-auth-drawer__close-line--bottom { transform: translateY(-3.25px) rotate(-45deg); }

.cobuntu-auth-drawer__nav {
  flex: 1;
  overflow-y: auto;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  /*
   * A VISIBLE scrollbar, which is the plain answer to "nothing says this
   * scrolls". macOS draws an overlay scrollbar that is invisible until you
   * are already scrolling, so at rest there is no affordance at all. Giving
   * ::-webkit-scrollbar any styling opts out of that overlay behaviour and
   * puts a classic, always-present scrollbar back.
   *
   * It is self-limiting in the way the fade has to be wired up for by hand:
   * a scrollbar only exists when the content actually overflows, so a list
   * that fits shows nothing.
   *
   * Sized off currentColor so it holds on any community palette and in both
   * themes, rather than a fixed grey that vanishes on a dark brand.
   */
  /* NOTE: deliberately NOT scrollbar-width / scrollbar-color. Setting either
     standard property switches Chrome to the standard scrollbar path and makes
     it IGNORE the ::-webkit-scrollbar rules below, which on macOS leaves the
     invisible overlay scrollbar in place -- measured: the nav kept a 0px
     gutter while overflowing. The webkit pseudo-elements alone are what opt
     out of the overlay and reserve real width. */
}
.cobuntu-auth-drawer__nav::-webkit-scrollbar {
  width: 10px;
}
.cobuntu-auth-drawer__nav::-webkit-scrollbar-track {
  background: transparent;
}
.cobuntu-auth-drawer__nav::-webkit-scrollbar-thumb {
  background: color-mix(in srgb, currentColor 22%, transparent);
  border-radius: 999px;
  /* Transparent border + padding-box clip insets the thumb inside the 10px
     gutter, so it reads as a slim pill rather than a full-width bar. */
  border: 3px solid transparent;
  background-clip: padding-box;
}
.cobuntu-auth-drawer__nav::-webkit-scrollbar-thumb:hover {
  background: color-mix(in srgb, currentColor 34%, transparent);
  background-clip: padding-box;
}
/*
 * Scroll affordance.
 *
 * The menu outgrew the panel — with API and Legal at the bottom, the two
 * newest rows were the ones nobody could see, and nothing on screen said the
 * list continued. The scrollbar is an overlay one on macOS, so it is invisible
 * until you are already scrolling, which is too late to be an affordance.
 *
 * A MASK rather than an overlaid gradient, for two reasons. The panel colour
 * is per-community themed (and mirrored to <html> at runtime), so an overlay
 * would need to track it or smear on a dark brand; a mask fades the real
 * content and needs no colour at all. And an overlay would sit exactly on the
 * last row's click target, where a missed pointer-events:none is invisible in
 * review and maddening in use. A mask cannot intercept a click.
 *
 * NOTE: this block lives inside a JS template literal, so no backticks and no
 * dollar-brace sequences in here. Both terminate the stylesheet silently.
 *
 * data-fade is maintained in JS from the scroll position, so the hint is
 * only ever present on an edge that actually has more content behind it. A
 * bottom fade that persists at the end of the list is decoration that lies.
 */
.cobuntu-auth-drawer__nav[data-fade="top"] {
  -webkit-mask-image: linear-gradient(to bottom, transparent 0, #000 32px);
  mask-image: linear-gradient(to bottom, transparent 0, #000 32px);
}
.cobuntu-auth-drawer__nav[data-fade="bottom"] {
  -webkit-mask-image: linear-gradient(to bottom, #000 calc(100% - 32px), transparent 100%);
  mask-image: linear-gradient(to bottom, #000 calc(100% - 32px), transparent 100%);
}
.cobuntu-auth-drawer__nav[data-fade="both"] {
  -webkit-mask-image: linear-gradient(to bottom, transparent 0, #000 32px, #000 calc(100% - 32px), transparent 100%);
  mask-image: linear-gradient(to bottom, transparent 0, #000 32px, #000 calc(100% - 32px), transparent 100%);
}
/*
 * Two-line link layout — icon + label + sublabel.
 * Matches components/panels/ProfilePanel.tsx in cobuntu-community-app
 * so visitors see the same drawer regardless of which surface drew it
 * (vanilla widget on the landing root vs cobuntu page on /login).
 * Updated 2026-05-25 from the previous single-line "My library / My
 * orders / Settings" set.
 */
/* Four headings over twelve rows. The list used to be twelve identically
   weighted bars, which gave the eye nothing to land on, and the per-row
   sub-labels that were supposed to help were also what pushed the last rows
   below the fold. Dropping them and grouping instead buys BOTH: the heading
   carries the context the sub-label used to, and the whole list now fits
   without scrolling. The mobile React drawer has been grouped all along
   (components/panels/account-drawer/RootMenu.tsx) — this is desktop catching
   up, not a new idea. */
.cobuntu-auth-drawer__group {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: color-mix(in srgb, currentColor 42%, transparent);
  padding: 13px 12px 4px;
}
.cobuntu-auth-drawer__group:first-child {
  padding-top: 2px;
}
.cobuntu-auth-drawer__link {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 7px 10px;
  border-radius: 14px;
  color: inherit;
  text-decoration: none;
  font-size: 14px;
  transition: background 0.12s ease;
}
/* The Legal sub-view keeps its sub-labels (it is four rows, it does not
   overflow, and there the sub-label IS the content). A two-line row needs
   more room than the one-line rows of the main menu. */
.cobuntu-auth-drawer__link:has(.cobuntu-auth-drawer__link-sub) {
  padding: 9px 10px;
}
.cobuntu-auth-drawer__link:hover {
  background: var(--cobuntu-drawer-hover, color-mix(in srgb, currentColor 5%, transparent));
}
.cobuntu-auth-drawer__link-chevron {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  opacity: 0.3;
}
.cobuntu-auth-drawer__subback {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 0;
  background: none;
  color: inherit;
  cursor: pointer;
  border-radius: 10px;
  transition: background 0.12s ease;
}
.cobuntu-auth-drawer__subback:hover {
  background: color-mix(in srgb, currentColor 12%, transparent);
}
/* Slide the whole drawer content when switching between the main menu and the
   Legal sub-view. Push = the new screen enters from the right (drilling in);
   pop (Back) = from the left. Applied to the direct children so the header and
   nav move together as one screen. Non-slide re-renders (initial open, the async
   profile refresh) set no data-anim and so don't animate. */
@keyframes cobuntu-auth-view-push {
  from { transform: translateX(32px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}
@keyframes cobuntu-auth-view-pop {
  from { transform: translateX(-32px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}
.cobuntu-auth-drawer[data-anim="push"] > * { animation: cobuntu-auth-view-push 0.22s cubic-bezier(0.16, 1, 0.3, 1) both; }
.cobuntu-auth-drawer[data-anim="pop"] > * { animation: cobuntu-auth-view-pop 0.22s cubic-bezier(0.16, 1, 0.3, 1) both; }
@media (prefers-reduced-motion: reduce) {
  .cobuntu-auth-drawer[data-anim] > * { animation: none; }
}
/* Each menu icon sits in a soft rounded card; it lights up in the brand colour
   on row hover. */
.cobuntu-auth-drawer__link-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 10px;
  background: color-mix(in srgb, currentColor 6%, transparent);
  transition: background 0.12s ease, color 0.12s ease;
}
.cobuntu-auth-drawer__link:hover .cobuntu-auth-drawer__link-icon {
  background: color-mix(in srgb, var(--cobuntu-accent, currentColor) 16%, transparent);
  color: var(--cobuntu-accent, currentColor);
}
.cobuntu-auth-drawer__link-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.cobuntu-auth-drawer__link-label {
  font-size: 14.5px;
  font-weight: 600;
  line-height: 1.3;
}
.cobuntu-auth-drawer__link-sub {
  font-size: 12px;
  line-height: 1.3;
  margin-top: 1px;
  opacity: 0.5;
}

.cobuntu-auth-drawer__footer {
  padding: 14px 16px 16px;
  border-top: 1px solid var(--cobuntu-drawer-border, color-mix(in srgb, currentColor 10%, transparent));
}
/* Sign out — a soft filled pill (not an old outlined box) that hints red on
   hover. */
.cobuntu-auth-drawer__signout {
  font: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 13px 16px;
  background: color-mix(in srgb, currentColor 6%, transparent);
  color: inherit;
  border: 0;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.cobuntu-auth-drawer__signout:hover {
  background: color-mix(in srgb, #ef4444 12%, transparent);
  color: #ef4444;
}
/* Build line — matches the mobile drawer's treatment (11px, centered, faded)
   so the same information reads the same way on both. Nothing here disables
   text selection: the whole point is that it can be copied into a bug
   report. */
.cobuntu-auth-drawer__build {
  margin: 10px 0 0;
  font-size: 11px;
  text-align: center;
  opacity: 0.45;
}

/* Loading skeleton — shown in the logged-in button while /me resolves (the JWT
   carries no name/photo). A shimmer avatar + label bar reads as "identity
   loading" instead of the old flash of "Account" + a generic persona. Colors
   are currentColor-derived so it works on any community header. Cleared by the
   fetchProfile().then swap; falls back to persona + "Account" if /me fails. */
.cobuntu-auth-skeleton {
  position: relative;
  overflow: hidden;
  background: color-mix(in srgb, currentColor 13%, transparent);
}
.cobuntu-auth-skeleton::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    transparent,
    color-mix(in srgb, currentColor 16%, transparent),
    transparent
  );
  transform: translateX(-100%);
  animation: cobuntu-auth-shimmer 1.4s ease-in-out infinite;
}
.cobuntu-auth-skeleton--avatar {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 9999px;
}
.cobuntu-auth-skeleton--label {
  display: inline-block;
  width: 68px;
  height: 10px;
  border-radius: 5px;
  vertical-align: middle;
}
@keyframes cobuntu-auth-shimmer {
  to { transform: translateX(100%); }
}
@media (prefers-reduced-motion: reduce) {
  .cobuntu-auth-skeleton::after { animation: none; }
}
`;

function injectStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = CSS;
  document.head.appendChild(el);
}

// ─── Mount management ─────────────────────────────────────────────

function mountAll(): void {
  // Do not paint before the session is known (S3). renderHost marks hosts with
  // MOUNTED_ATTR and mountAll skips marked ones, so a render with an unknown
  // session STICKS — here that means a Sign-in button shown to a signed-in
  // member until they reload. Deferring inside mountAll also covers the
  // MutationObserver path; primeSession() is cached, so re-entry is free.
  if (getCachedSession() === null) {
    void primeSession().then(mountAll);
    return;
  }
  document
    .querySelectorAll<HTMLElement>(`${MOUNT_SELECTOR}:not([${MOUNTED_ATTR}])`)
    .forEach(renderHost);
}

let observer: MutationObserver | null = null;
function watchDom(): void {
  if (observer) return;
  observer = new MutationObserver(mountAll);
  observer.observe(document.body, { childList: true, subtree: true });
}

// ─── Boot ─────────────────────────────────────────────────────────

function init(): void {
  injectStyles();
  mountAll();
  watchDom();
  // Re-render on auth changes from other tabs.
  window.addEventListener("storage", (e) => {
    if (e.key === PROFILE_CACHE_KEY) rerenderAll();
  });
}

if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
}

// ─── Public API ───────────────────────────────────────────────────

declare global {
  interface Window {
    CobuntuAuth?: {
      isAuthenticated: () => boolean;
      openProfile: () => void;
      closeProfile: () => void;
      signOut: () => void;
      refresh: () => void;
    };
  }
}

if (typeof window !== "undefined") {
  window.CobuntuAuth = {
    isAuthenticated,
    openProfile: openDrawer,
    closeProfile: closeDrawer,
    signOut: handleSignOut,
    refresh: rerenderAll,
  };
}

// ─── Test exports ─────────────────────────────────────────────────

export const __test = {
  isAuthenticated,
  initials,
  escapeHtml,
  premiumBadgeMarkup,
  readCachedProfile,
  hasKnownIdentity,
  PROFILE_CACHE_KEY,
  PROFILE_TTL_MS,
  COOKIE_NAME,
  MOUNT_SELECTOR,
};
